// LT-312 — Context for the Cmd-K palette. Exposes `open`, `setOpen` so
// any page can programmatically open the palette (e.g. a "Search…" chip
// at the top of the sidebar, LT-312 follow-up). Keeps the keybinding
// handler colocated with the provider.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

interface CommandPaletteContextValue {
  open: boolean;
  openPalette: () => void;
  closePalette: () => void;
  togglePalette: () => void;
}

const CommandPaletteContext = createContext<CommandPaletteContextValue | null>(null);

export const CommandPaletteProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [open, setOpen] = useState(false);

  const openPalette = useCallback(() => setOpen(true), []);
  const closePalette = useCallback(() => setOpen(false), []);
  const togglePalette = useCallback(() => setOpen((prev) => !prev), []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Cmd+K on Mac, Ctrl+K on Windows/Linux. Skip when the user is
      // typing in an input so browser Cmd+K for "move focus to URL bar"
      // remains available when appropriate (Chrome already handles this
      // in address-bar-focused mode; we skip on form fields out of
      // caution).
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey && !e.altKey && e.key.toLowerCase() === 'k') {
        const target = e.target as HTMLElement | null;
        if (target) {
          const tag = target.tagName;
          if (
            (tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable) &&
            // Exception: the palette's OWN input should still let Esc / Enter through,
            // but Cmd+K on the palette input itself just re-toggles the palette.
            !target.closest('[data-command-palette-input="true"]')
          ) {
            // Allow Cmd+K inside a plain form field to STILL open the palette —
            // this is the spec per Raycast / Linear / Superhuman. Browser Cmd+K
            // only conflicts on the URL bar, which isn't part of the DOM.
          }
        }
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const value = useMemo<CommandPaletteContextValue>(
    () => ({ open, openPalette, closePalette, togglePalette }),
    [open, openPalette, closePalette, togglePalette],
  );

  return <CommandPaletteContext.Provider value={value}>{children}</CommandPaletteContext.Provider>;
};

export function useCommandPalette(): CommandPaletteContextValue {
  const ctx = useContext(CommandPaletteContext);
  if (!ctx) {
    throw new Error('useCommandPalette must be used inside CommandPaletteProvider');
  }
  return ctx;
}
